import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import { useState } from "react";
import { GREEN, YELLOW, RED, BLUE } from "@helper/colors";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import { Formik, Form } from "formik";
import { updateTreeThreshold } from "@modules/Trees/services/treeCrud";
import { useMutation, useQueryClient } from "react-query";

type UpdateTreeThresholdPayload = {
  id: number;
  formData: {
    yellow_threshold: number;
    green_threshold: number;
    blue_threshold: number;
  };
};

type ProjectCache = {
  areas?: {
    tree_set?: any[];
  }[];
};

type TreesPaginated = {
  results: any[];
};

type TreesCache = TreesPaginated | any[];

const marks = (thresholds) => {
  return [
    {
      value: thresholds[0],
      label: `${thresholds[0]}%`,
      name: "yellow",
    },
    {
      value: thresholds[1],
      label: `${thresholds[1]}%`,
      name: "green",
    },
    {
      value: thresholds[2],
      label: `${thresholds[2]}%`,
      name: "blue",
    },
  ];
};

function valuetext(value: number) {
  return `${value}%`;
}

function HealthThresholdSlider({ tree, setEdit }) {
  console.log("tree", tree);
  const [thresholds, setThresholds] = useState<number[]>([
    tree ? tree.yellow_threshold : 50,
    tree ? tree.green_threshold : 70,
    tree ? tree.blue_threshold : 100,
  ]);
  const initialMarks = marks(thresholds);

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { isLoading, mutateAsync: mutateAsyncTree } = useMutation<
    any,
    Error,
    UpdateTreeThresholdPayload
  >(
    "updateTreeThreshold",
    updateTreeThreshold, // TODO: function to create Tree with Area and Sensors
    {
      onSuccess: () => {
        // Update project cache to reflect threshold changes in tree_set
        const projectQueries = queryClient.getQueriesData({
          predicate: (query) => query.queryKey[0] === "project",
        });

        projectQueries.forEach(([queryKey, projectData]) => {
          if (projectData) {
            queryClient.setQueryData(queryKey, (oldProject: ProjectCache | undefined) => {
              if (!oldProject) return oldProject;

              return {
                ...oldProject,
                areas:
                  oldProject.areas?.map((area) => ({
                    ...area,
                    tree_set:
                      area.tree_set?.map((treeItem) => {
                        if (treeItem.id === tree.id) {
                          return {
                            ...treeItem,
                            yellow_threshold: thresholds[0],
                            green_threshold: thresholds[1],
                            blue_threshold: thresholds[2],
                          };
                        }
                        return treeItem;
                      }) ?? [],
                  })) ?? [],
              };
            });
          }
        });

        // Update trees query cache for the trees list
        const treesQueries = queryClient.getQueriesData({
          predicate: (query) => query.queryKey[0] === "trees",
        });
        treesQueries.forEach(([queryKey, treesData]) => {
          if (treesData) {
            queryClient.setQueryData(queryKey, (oldTrees: TreesCache | undefined) => {
              if (!oldTrees) return oldTrees;

              // Handle both paginated and non-paginated responses
              if (Array.isArray(oldTrees)) {
                return oldTrees.map((treeItem) =>
                  treeItem.id === tree.id
                    ? {
                        ...treeItem,
                        yellow_threshold: thresholds[0],
                        green_threshold: thresholds[1],
                        blue_threshold: thresholds[2],
                      }
                    : treeItem
                );
              }

              return {
                ...oldTrees,
                results: oldTrees.results.map((treeItem) =>
                  treeItem.id === tree.id
                    ? {
                        ...treeItem,
                        yellow_threshold: thresholds[0],
                        green_threshold: thresholds[1],
                        blue_threshold: thresholds[2],
                      }
                    : treeItem
                ),
              };
            });
          }
        });

        // Still invalidate the specific tree query for other components
        queryClient.invalidateQueries(["tree", `${tree.id}`]);
        queryClient.invalidateQueries("trees");

        setEdit(false);
      },
    }
  );
  const initialValues = {
    yellow_threshold: thresholds[0],
    green_threshold: thresholds[1],
    blue_threshold: thresholds[2],
  };

  const handleSubmit = async (values) => {
    console.log("HANDLING SUBMIT");
    console.log("VALUES", values);
    const treeFormData = {
      yellow_threshold: thresholds[0],
      green_threshold: thresholds[1],
      blue_threshold: thresholds[2],
    };
    await mutateAsyncTree({ formData: treeFormData, id: tree.id });
  };
  return (
    <Formik
      initialValues={initialValues}
      validate={() => {
        const errors = {};

        return errors;
      }}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          <Box
            sx={{
              width: 400,
            }}
          >
            <Box
              sx={{
                position: "relative",
                height: "20px",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: "0%",
                  width: `${thresholds[0]}%`,
                  height: "100%",
                  backgroundColor: RED,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  left: `${thresholds[0]}%`,
                  width: `${thresholds[1] - thresholds[0]}%`,
                  height: "100%",
                  backgroundColor: YELLOW,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  left: `${thresholds[1]}%`,
                  width: `${thresholds[2] - thresholds[1]}%`,
                  height: "100%",
                  backgroundColor: GREEN,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  left: `${thresholds[2]}%`,
                  width: `${100 - thresholds[2]}%`,
                  height: "100%",
                  backgroundColor: BLUE,
                }}
              />
              <Slider
                // track={false}
                aria-labelledby="track-false-range-slider"
                getAriaValueText={valuetext}
                defaultValue={thresholds}
                onChange={(event: Event, newValue: number | number[]) => {
                  if (Array.isArray(newValue)) {
                    const uniqueValues = Array.from(new Set(newValue));
                    if (uniqueValues.length === newValue.length) {
                      console.log(uniqueValues, newValue);
                      setThresholds(newValue);
                    }
                  }
                }}
                marks={initialMarks}
                value={thresholds}
                step={1}
              />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 7 }}>
              <Button
                style={{ justifyContent: "flex-start" }}
                onClick={() => setThresholds([50, 70, 100])}
              >
                {t("set_default")}
              </Button>
              <Button
                style={{
                  justifyContent: "flex-end",
                  alignItems: "flex-end",
                  alignSelf: "flex-end",
                }}
                type="submit"
              >
                {isLoading ? t("button.updating") : t("button.update")}
              </Button>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
}

export default HealthThresholdSlider;
